import moment from 'moment';
import {Color, Product, Rgb} from '../models/product';
import {StoreProduct} from '../models/storeProduct';

type ObjectType<T = unknown> = Record<string, T>;

function isObject(item: unknown): boolean {
  return item && typeof item === 'object' && !Array.isArray(item);
}

function mergeDeep(target: ObjectType, source: ObjectType): ObjectType {
  const output = {...target};
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) Object.assign(output, {[key]: source[key]});
        else
          output[key] = mergeDeep(
            target[key] as ObjectType,
            source[key] as ObjectType
          );
      } else {
        Object.assign(output, {[key]: source[key]});
      }
    });
  }
  return output;
}

function convertStringToFloat(value: string | number): number | null {
  if (value != null) {
    return parseFloat(<string>value);
  }
  return null;
}

const coreFormatString = 'ddd[.,] MMM[.] D[,] YYYY';

function getCoreDateFormat(date: string | moment.Moment): string {
  return moment(date).format(coreFormatString);
}

const swapEditPath = (path: string): string => {
  return path.replace('/edit', '/manage');
};

function getProductColors(
  storeProduct: StoreProduct,
  mmsProducts: Product[]
): Color[] {
  const enabledColorIds = storeProduct.variants
    .filter(v => v.enabled)
    .map(v => v.colorId);
  const mmsProduct = mmsProducts?.find(p => p.id === storeProduct.styleId);
  if (mmsProduct)
    return enabledColorIds.map(colorId =>
      mmsProduct.colors.find(c => c.id === colorId)
    );
  return [];
}
const componentToHex = (c: number): string => {
  if (c === null || c === undefined || Number.isNaN(c) || c < 0 || c > 255) {
    return '';
  }
  const hex = c.toString(16);
  return hex.length === 1 ? `0${hex}` : hex;
};
const rgbToHex = (rgb: Rgb): string => {
  const {red, green, blue} = rgb;
  const hexRed = componentToHex(red);
  const hexGreen = componentToHex(green);
  const hexBlue = componentToHex(blue);
  return `#${hexRed}${hexGreen}${hexBlue}`;
};

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

const formatCurrency = (price: number | string): string => {
  const num = Number(price);
  return Number.isNaN(num) ? String(price) : formatter.format(num);
};

const campaignDaysLeft = (time: string): string => {
  if (!time) return '';
  if (time.includes('Less than an hour')) return time.toLowerCase();

  const timeArray = time.split(' ');
  timeArray.splice(1, 0, 'more');
  const timeLeft = timeArray.join(' ');
  return timeLeft;
};

function toCamelCase<T>(obj: Record<string, any>): T {
  const result: any = {};
  Object.keys(obj).forEach(key => {
    const camelCaseKey: string = key.replace(/_([a-z0-9])/g, g =>
      g[1].toUpperCase()
    );
    result[camelCaseKey] = obj[key];
  });
  return result as T;
}

export {
  isObject,
  mergeDeep,
  convertStringToFloat,
  getCoreDateFormat,
  rgbToHex,
  swapEditPath,
  getProductColors,
  formatCurrency,
  campaignDaysLeft,
  toCamelCase
};
